import { useEffect, useMemo, useState } from "react";
import { ApolloError } from "@apollo/client";
import { useGetFootnoteCustomFieldsQuery } from "~/graphql/supergraphHooks";
import type { CustomFieldOption, CustomFieldOptionGroup } from "./types";

/**
 * Custom-field source for the footnote settings panel.
 *
 * A `CUSTOM_FIELD` footnote pulls its per-investor text from a GPX custom
 * field. The Semantic Layer appends the arena's custom fields to the modeled
 * catalog as synthetic columns (each carrying `customFieldId` /
 * `customFieldName` / `customFieldAppliesTo`) when `finpubQueryAttributes` is
 * queried with an `arenaId`.
 *
 * Because the custom fields are appended *after* the modeled catalog, a single
 * page can be entirely modeled columns — so we page through every attribute
 * (following `pagination.hasMore`) and keep only the custom-field ones.
 */

/** The Semantic Layer caps a page at 1000 rows (`PAGINATION_LIMIT_MAX`). */
const ATTRIBUTES_PAGE_LIMIT = 1000;

/** Picker section label per `applies_to` object type, in display order. The
 * warehouse records `InvestorAccount`, which a report author knows as an
 * entity. `Offering` / `Prospect` custom fields never reach the picker (the
 * Semantic Layer filters them out of the catalog). */
const SECTION_LABEL_BY_APPLIES_TO: ReadonlyArray<readonly [string, string]> = [
  ["Contact", "Contact"],
  ["InvestorAccount", "Entity"],
  ["Position", "Position"],
];

/** Section for custom fields whose object type the catalog didn't report. */
const OTHER_SECTION_LABEL = "Other";

export type UseFootnoteCustomFieldsResult = {
  /** The arena's custom fields, split into the picker's object-type sections. */
  customFieldOptionGroups: ReadonlyArray<CustomFieldOptionGroup>;
  isLoading: boolean;
  /** Set when the initial query or a paging request fails; the caller surfaces
   * it instead of showing a silently-empty picker. */
  error?: ApolloError;
};

/** Group options by `appliesTo`, ordered by object type then name. Unknown /
 * missing object types collect into a trailing "Other" section. */
export const groupCustomFieldOptions = (
  options: ReadonlyArray<CustomFieldOption>,
): ReadonlyArray<CustomFieldOptionGroup> => {
  const groups: CustomFieldOptionGroup[] = [];
  for (const [appliesTo, sectionLabel] of SECTION_LABEL_BY_APPLIES_TO) {
    const matching = options.filter((option) => option.appliesTo === appliesTo);
    if (matching.length > 0) groups.push({ sectionLabel, options: matching });
  }

  const known = new Set(SECTION_LABEL_BY_APPLIES_TO.map(([appliesTo]) => appliesTo));
  const other = options.filter(
    (option) => option.appliesTo == null || !known.has(option.appliesTo),
  );
  if (other.length > 0) groups.push({ sectionLabel: OTHER_SECTION_LABEL, options: other });

  return groups;
};

/** Fetch the arena's GPX custom fields as footnote picker options. Returns an
 * empty list until an `arenaId` is known (the catalog omits custom fields
 * without one).
 *
 * `enabled` defers the fetch until the data is actually needed — the caller
 * gates it on the footnote settings panel being open */
export const useFootnoteCustomFields = (
  arenaId: number | null,
  enabled = true,
): UseFootnoteCustomFieldsResult => {
  const {
    data,
    loading,
    error: queryError,
    fetchMore,
  } = useGetFootnoteCustomFieldsQuery({
    variables: { arenaId, limit: ATTRIBUTES_PAGE_LIMIT, offset: 0 },
    skip: arenaId == null || !enabled,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // A failed page fetch stops pagination: surfacing the error (and keeping the
  // pages we already have)
  const [pageError, setPageError] = useState<ApolloError>();

  const loadedCount = data?.finpubQueryAttributes?.attributes.length ?? 0;
  const morePages = data?.finpubQueryAttributes?.pagination.hasMore ?? false;
  const error = queryError ?? pageError;
  const hasMore = morePages && error == null;

  // Walk the remaining pages, concatenating each into the cached result so the
  // memo below sees every attribute (custom fields sit on the last page).
  useEffect(() => {
    if (!hasMore) return;
    let active = true;
    void fetchMore({
      variables: { offset: loadedCount },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          finpubQueryAttributes: {
            ...fetchMoreResult.finpubQueryAttributes,
            attributes: [
              ...prev.finpubQueryAttributes.attributes,
              ...fetchMoreResult.finpubQueryAttributes.attributes,
            ],
          },
        };
      },
    }).catch((err: unknown) => {
      if (active) {
        setPageError(
          err instanceof ApolloError ? err : new ApolloError({ errorMessage: String(err) }),
        );
      }
    });
    return () => {
      active = false;
    };
  }, [hasMore, loadedCount, fetchMore]);

  const customFieldOptions = useMemo<ReadonlyArray<CustomFieldOption>>(() => {
    const attributes = data?.finpubQueryAttributes?.attributes ?? [];
    return attributes
      .filter(
        (attr): attr is typeof attr & { customFieldId: string } => attr.customFieldId != null,
      )
      .map((attr) => ({
        id: attr.customFieldId,
        customFieldId: attr.customFieldId,
        name: attr.customFieldName?.trim() || attr.name,
        appliesTo: attr.customFieldAppliesTo?.trim() || null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const customFieldOptionGroups = useMemo(
    () => groupCustomFieldOptions(customFieldOptions),
    [customFieldOptions],
  );

  // Still "loading" while more pages remain, so the picker doesn't briefly
  // render an incomplete list.
  return { customFieldOptionGroups, isLoading: loading || hasMore, error };
};
